// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { useEffect } from 'react';
import { IconArrowLeft } from 'components/atoms/icons/IconArrowLeft';
import { IconArrowRight } from 'components/atoms/icons/IconArrowRight';
import { downloadFileFromUrl } from 'util/commons';
import styles from './ImageViewerModal.module.scss';

interface ImageViewerModalProps {
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export const ImageViewerModal = ({
  images,
  currentIndex,
  onClose,
  onIndexChange,
}: ImageViewerModalProps) => {
  // Keyboard event for modal navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (e.key === 'ArrowLeft') {
        onIndexChange((currentIndex - 1 + images.length) % images.length);
      } else if (e.key === 'ArrowRight') {
        onIndexChange((currentIndex + 1) % images.length);
      } else if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, images.length, onClose, onIndexChange]);

  const handlePrev = () => {
    onIndexChange((currentIndex - 1 + images.length) % images.length);
  };

  const handleNext = () => {
    onIndexChange((currentIndex + 1) % images.length);
  };

  const handleDownload = () => {
    downloadFileFromUrl(images[currentIndex]);
  };

  return (
    <div className={styles.modal} onClick={onClose}>
      <button className={styles.close} onClick={onClose}>
        ×
      </button>

      <button
        className={styles.downloadIcon}
        onClick={(e) => {
          e.stopPropagation();
          handleDownload();
        }}
        title="ダウンロード"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M7 10L12 15L17 10"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 15V3"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {images.length > 1 && (
        <div className={styles.indicator}>
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Prev arrow */}
      {images.length > 1 && (
        <button
          className={`${styles.arrow} ${styles.arrowLeft}`}
          onClick={(e) => {
            e.stopPropagation();
            handlePrev();
          }}
        >
          <IconArrowLeft color="#fff" size={28} />
        </button>
      )}

      {/* Main image */}
      <div className={styles.imageWrapper} onClick={(e) => e.stopPropagation()}>
        <img src={images[currentIndex]} alt="detail" className={styles.img} />
      </div>

      {/* Next arrow */}
      {images.length > 1 && (
        <button
          className={`${styles.arrow} ${styles.arrowRight}`}
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
        >
          <IconArrowRight color="#fff" size={28} />
        </button>
      )}

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className={styles.thumbnails} onClick={(e) => e.stopPropagation()}>
          {images.map((img, idx) => (
            <img
              key={idx}
              src={img}
              alt={`thumb-${idx}`}
              className={`${styles.thumbnail} ${
                idx === currentIndex ? styles.active : ''
              }`}
              onClick={() => onIndexChange(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
